package top.wcpe.allincore.ui.web

import top.wcpe.allincore.ui.runtime.render.model.RenderCommandType

internal enum class WebRenderCapability(val wireValue: String) {
    NATIVE("native"),
    PLACEHOLDER("placeholder"),
    UNSUPPORTED("unsupported"),
}

private fun webRenderSignature(name: String, vararg params: String, returns: String = U): String =
    "UiRenderBridge.$name(${params.joinToString(",")}):$returns"

private const val U = "kotlin.Unit"
private const val B = "kotlin.Boolean"
private const val I = "kotlin.Int"
private const val F = "kotlin.Float"
private const val S = "kotlin.String"
private const val NS = "kotlin.String?"
private const val FA = "kotlin.FloatArray"
private const val NFA = "kotlin.FloatArray?"
private const val BRIDGE = "top.wcpe.allincore.ui.runtime.render.UiRenderBridge"
private const val IMAGE_STYLE = "top.wcpe.allincore.ui.runtime.render.model.ImageStyle?"
private const val BATCH = "top.wcpe.allincore.ui.runtime.render.model.RenderBatch"
private const val MAP = "kotlin.collections.Map<kotlin.String, *>"

internal object WebRenderCapabilityKeys {
    val FILL_RECT = webRenderSignature("fillRect", F, F, F, F, I)
    val DRAW_TEXT_5 = webRenderSignature("drawText", S, F, F, I, F)
    val DRAW_TEXT_10 = webRenderSignature("drawText", S, F, F, I, F, NS, B, B, B)
    val SUPPORTS_RICH_TEXT = webRenderSignature("supportsRichText", returns = B)
    val MEASURE_RICH_TEXT = webRenderSignature("measureRichText", S, F, NS, B, F, B, returns = FA)
    val DRAW_LINE = webRenderSignature("drawLine", F, F, F, F, F, I)
    val PUSH_CLIP = webRenderSignature("pushClip", F, F, F, F)
    val POP_CLIP = webRenderSignature("popClip")
    val SUPPORTS_RUNTIME_TRANSFORM = webRenderSignature("supportsRuntimeTransform", returns = B)
    val PUSH_TRANSFORM = webRenderSignature("pushTransform", F, F, F, F)
    val POP_TRANSFORM = webRenderSignature("popTransform")
    val SUPPORTS_RUNTIME_ALPHA = webRenderSignature("supportsRuntimeAlpha", returns = B)
    val PUSH_ALPHA = webRenderSignature("pushAlpha", F)
    val POP_ALPHA = webRenderSignature("popAlpha")
    val SUPPORTS_COLOR_MATRIX = webRenderSignature("supportsColorMatrix", returns = B)
    val SUPPORTS_FULL_TRANSFORM = webRenderSignature("supportsFullTransform", returns = B)
    val SUPPORTS_FILTER_GROUP = webRenderSignature("supportsFilterGroup", returns = B)
    val SUPPORTS_BACKDROP_FILTER = webRenderSignature("supportsBackdropFilter", returns = B)
    val SUPPORTS_BLEND_MODE = webRenderSignature("supportsBlendMode", returns = B)
    val PUSH_Z = webRenderSignature("pushZ", F)
    val POP_Z = webRenderSignature("popZ")
    val FLUSH = webRenderSignature("flush")
    val TEXT_WIDTH_2 = webRenderSignature("textWidth", S, F, returns = F)
    val TEXT_WIDTH_5 = webRenderSignature("textWidth", S, F, NS, B, returns = F)
    val SCREEN_WIDTH = webRenderSignature("screenWidth", returns = F)
    val SCREEN_HEIGHT = webRenderSignature("screenHeight", returns = F)
    val TEXTURE_EXISTS = webRenderSignature("textureExists", S, returns = B)
    val SCREEN_SPACE_BRIDGE = webRenderSignature("screenSpaceBridge", returns = BRIDGE)
    val SUPPORTS_BATCHING = webRenderSignature("supportsBatching", returns = B)
    val MAX_SHADOWED_ELEMENTS = webRenderSignature("maxShadowedElementsPerFrame", returns = I)
    val DRAW_IMAGE = webRenderSignature("drawImage", S, F, F, F, F)
    val DRAW_TEXTURED_QUAD = webRenderSignature("drawTexturedQuad", S, F, F, F, F, F, F, F, F)
    val SUBMIT_BATCH = webRenderSignature("submitBatch", BATCH)

    val DRAW_RICH_TEXT = webRenderSignature("drawRichText", S, F, F, I, F, NS, B, B, B, F, B, F)
    val DRAW_ITEM_STACK_6 = webRenderSignature("drawItemStack", S, I, F, F, F, F)
    val DRAW_ITEM_STACK_7 = webRenderSignature("drawItemStack", S, I, B, F, F, F, F)
    val DRAW_ITEM_STACK_8 = webRenderSignature("drawItemStack", S, I, B, B, F, F, F, F)
    val DRAW_ITEM_STACK_12 = webRenderSignature("drawItemStack", S, I, B, B, NS, F, F, F, F, F, F)
    val DRAW_ITEM_STACK_13 = webRenderSignature("drawItemStack", S, NS, I, B, B, NS, F, F, F, F, F, F)
    val DRAW_ITEM_STACK_14 = webRenderSignature("drawItemStack", S, NS, I, B, B, NS, F, F, I, F, F, F, F)
    val DRAW_ENTITY_PREVIEW_7 = webRenderSignature("drawEntityPreview", S, F, F, F, F, F, F)
    val DRAW_ENTITY_PREVIEW_12 = webRenderSignature("drawEntityPreview", S, NS, F, F, F, F, F, F, F, F, F, B)
    val DRAW_ENTITY_PREVIEW_13 = webRenderSignature("drawEntityPreview", S, NS, NS, F, F, F, F, F, F, F, F, F, B)
    val DRAW_MODEL_PREVIEW = webRenderSignature("drawModelPreview", S, S, NS, S, F, F, F, F, F, F, F, B, I)

    val DRAW_IMAGE_STYLED = webRenderSignature("drawImageStyled", S, F, F, F, F, IMAGE_STYLE)
    val PUSH_COLOR_MATRIX = webRenderSignature("pushColorMatrix", FA)
    val POP_COLOR_MATRIX = webRenderSignature("popColorMatrix")
    val PUSH_TRANSFORM_3X3 = webRenderSignature("pushTransform3x3", FA)
    val POP_TRANSFORM_3X3 = webRenderSignature("popTransform3x3")
    val PUSH_TRANSFORM_4X4 = webRenderSignature("pushTransform4x4", FA)
    val POP_TRANSFORM_4X4 = webRenderSignature("popTransform4x4")
    val BEGIN_FILTER_GROUP = webRenderSignature("beginFilterGroup", F, F, F, F, F)
    val END_FILTER_GROUP = webRenderSignature("endFilterGroup")
    val APPLY_BACKDROP_BLUR = webRenderSignature("applyBackdropBlur", F, F, F, F, F)
    val PUSH_BLEND_MODE = webRenderSignature("pushBlendMode", I)
    val POP_BLEND_MODE = webRenderSignature("popBlendMode")
    val APPLY_NEON_GLOW = webRenderSignature("applyNeonGlow", F, F, F, F, I, F, F, B, NFA)
    val APPLY_RIPPLE_9 = webRenderSignature("applyRippleEffect", F, F, F, F, I, F, F, F, F)
    val APPLY_RIPPLE_10 = webRenderSignature("applyRippleEffect", F, F, F, F, I, F, F, F, F, NFA)
    val APPLY_PROCEDURAL_TEXTURE = webRenderSignature("applyProceduralTexture", F, F, F, F, NS, I, I, F, F, F, NFA)
    val APPLY_FLOW = webRenderSignature("applyFlow", F, F, F, F, I, F, F, F, I, NFA)
    val APPLY_ENERGY = webRenderSignature("applyEnergy", F, F, F, F, I, F, F, F, I, NFA)
    // master history/particle：Web 端暂不支持，记入冻结矩阵供诊断键校验
    val SUPPORTS_HISTORY_CAPTURE = webRenderSignature("supportsHistoryCapture", returns = B)
    val BEGIN_HISTORY_CAPTURE = webRenderSignature("beginHistoryCapture", S, MAP, F, F, F, F)
    val END_HISTORY_CAPTURE = webRenderSignature("endHistoryCapture")
    val SUPPORTS_PARTICLES = webRenderSignature("supportsParticles", returns = B)
    val DRAW_MATERIAL_LAYER = webRenderSignature("drawMaterialLayer", S, MAP, F, F, F, F)
    val DRAW_PARTICLE_LAYER = webRenderSignature("drawParticleLayer", S, I, MAP, F, F, F, F)
}

internal object WebRenderDiagnosticCapabilityKeys {
    const val SURFACE_INIT = "WebRenderSurface.init"
    const val SURFACE_RESIZE = "WebRenderSurface.resize"
    const val SURFACE_LOGICAL_VIEWPORT = "WebRenderSurface.setLogicalViewport"
    const val SURFACE_RENDER = "WebRenderSurface.renderFrame"
    const val SURFACE_DISPOSE = "WebRenderSurface.dispose"
    const val SURFACE_WEBGL = "WebRenderSurface.webgl"
    const val RESOURCE_DISPOSE = "WebRenderSurface.resourceDispose"
    const val MODEL_COMPOSITION = "WebRenderSurface.modelCompositionHost"

    val explanations: Map<String, String> = mapOf(
        SURFACE_INIT to "§3.1 surface 初始化状态机",
        SURFACE_RESIZE to "§3.1 surface 尺寸与 DPR",
        SURFACE_LOGICAL_VIEWPORT to "§3.1 surface 逻辑视口",
        SURFACE_RENDER to "§3.1 surface 帧提交",
        SURFACE_DISPOSE to "§3.1 surface 幂等销毁",
        SURFACE_WEBGL to "§6.1 Browser WebGL 可用性",
        RESOURCE_DISPOSE to "§3.2 surface 资源所有权",
        MODEL_COMPOSITION to "§3.3 modelCompositionHost 允许接缝",
    )

    fun contains(key: String): Boolean = explanations.containsKey(key)
}

internal object WebRenderCapabilityMatrix {

    const val VERSION = "1"

    private val nativeMethodKeys = listOf(
        WebRenderCapabilityKeys.FILL_RECT,
        WebRenderCapabilityKeys.DRAW_TEXT_5,
        WebRenderCapabilityKeys.DRAW_TEXT_10,
        WebRenderCapabilityKeys.SUPPORTS_RICH_TEXT,
        WebRenderCapabilityKeys.MEASURE_RICH_TEXT,
        WebRenderCapabilityKeys.DRAW_LINE,
        WebRenderCapabilityKeys.PUSH_CLIP,
        WebRenderCapabilityKeys.POP_CLIP,
        WebRenderCapabilityKeys.SUPPORTS_RUNTIME_TRANSFORM,
        WebRenderCapabilityKeys.PUSH_TRANSFORM,
        WebRenderCapabilityKeys.POP_TRANSFORM,
        WebRenderCapabilityKeys.SUPPORTS_RUNTIME_ALPHA,
        WebRenderCapabilityKeys.PUSH_ALPHA,
        WebRenderCapabilityKeys.POP_ALPHA,
        WebRenderCapabilityKeys.SUPPORTS_COLOR_MATRIX,
        WebRenderCapabilityKeys.SUPPORTS_FULL_TRANSFORM,
        WebRenderCapabilityKeys.SUPPORTS_FILTER_GROUP,
        WebRenderCapabilityKeys.SUPPORTS_BACKDROP_FILTER,
        WebRenderCapabilityKeys.SUPPORTS_BLEND_MODE,
        WebRenderCapabilityKeys.PUSH_Z,
        WebRenderCapabilityKeys.POP_Z,
        WebRenderCapabilityKeys.FLUSH,
        WebRenderCapabilityKeys.TEXT_WIDTH_2,
        WebRenderCapabilityKeys.TEXT_WIDTH_5,
        WebRenderCapabilityKeys.SCREEN_WIDTH,
        WebRenderCapabilityKeys.SCREEN_HEIGHT,
        WebRenderCapabilityKeys.TEXTURE_EXISTS,
        WebRenderCapabilityKeys.SCREEN_SPACE_BRIDGE,
        WebRenderCapabilityKeys.SUPPORTS_BATCHING,
        WebRenderCapabilityKeys.MAX_SHADOWED_ELEMENTS,
        WebRenderCapabilityKeys.DRAW_IMAGE,
        WebRenderCapabilityKeys.DRAW_TEXTURED_QUAD,
        WebRenderCapabilityKeys.SUBMIT_BATCH,
    )

    private val placeholderMethodKeys = listOf(
        WebRenderCapabilityKeys.DRAW_RICH_TEXT,
        WebRenderCapabilityKeys.DRAW_ITEM_STACK_6,
        WebRenderCapabilityKeys.DRAW_ITEM_STACK_7,
        WebRenderCapabilityKeys.DRAW_ITEM_STACK_8,
        WebRenderCapabilityKeys.DRAW_ITEM_STACK_12,
        WebRenderCapabilityKeys.DRAW_ITEM_STACK_13,
        WebRenderCapabilityKeys.DRAW_ITEM_STACK_14,
        WebRenderCapabilityKeys.DRAW_ENTITY_PREVIEW_7,
        WebRenderCapabilityKeys.DRAW_ENTITY_PREVIEW_12,
        WebRenderCapabilityKeys.DRAW_ENTITY_PREVIEW_13,
        WebRenderCapabilityKeys.DRAW_MODEL_PREVIEW,
    )

    private val unsupportedMethodKeys = listOf(
        WebRenderCapabilityKeys.DRAW_IMAGE_STYLED,
        WebRenderCapabilityKeys.PUSH_COLOR_MATRIX,
        WebRenderCapabilityKeys.POP_COLOR_MATRIX,
        WebRenderCapabilityKeys.PUSH_TRANSFORM_3X3,
        WebRenderCapabilityKeys.POP_TRANSFORM_3X3,
        WebRenderCapabilityKeys.PUSH_TRANSFORM_4X4,
        WebRenderCapabilityKeys.POP_TRANSFORM_4X4,
        WebRenderCapabilityKeys.BEGIN_FILTER_GROUP,
        WebRenderCapabilityKeys.END_FILTER_GROUP,
        WebRenderCapabilityKeys.APPLY_BACKDROP_BLUR,
        WebRenderCapabilityKeys.PUSH_BLEND_MODE,
        WebRenderCapabilityKeys.POP_BLEND_MODE,
        WebRenderCapabilityKeys.APPLY_NEON_GLOW,
        WebRenderCapabilityKeys.APPLY_RIPPLE_9,
        WebRenderCapabilityKeys.APPLY_RIPPLE_10,
        WebRenderCapabilityKeys.APPLY_PROCEDURAL_TEXTURE,
        WebRenderCapabilityKeys.APPLY_FLOW,
        WebRenderCapabilityKeys.APPLY_ENERGY,
        WebRenderCapabilityKeys.SUPPORTS_HISTORY_CAPTURE,
        WebRenderCapabilityKeys.BEGIN_HISTORY_CAPTURE,
        WebRenderCapabilityKeys.END_HISTORY_CAPTURE,
        WebRenderCapabilityKeys.SUPPORTS_PARTICLES,
        WebRenderCapabilityKeys.DRAW_MATERIAL_LAYER,
        WebRenderCapabilityKeys.DRAW_PARTICLE_LAYER,
    )

    val frozenMethodKeys: Set<String> = (
        nativeMethodKeys + placeholderMethodKeys + unsupportedMethodKeys
        ).toSet()

    private val methodCapabilities: Map<String, WebRenderCapability> = buildMap {
        nativeMethodKeys.forEach { put(it, WebRenderCapability.NATIVE) }
        placeholderMethodKeys.forEach { put(it, WebRenderCapability.PLACEHOLDER) }
        unsupportedMethodKeys.forEach { put(it, WebRenderCapability.UNSUPPORTED) }
    }.also { capabilities ->
        require(capabilities.keys == frozenMethodKeys) { "Web 渲染方法能力矩阵与冻结签名集合不一致" }
        require(frozenMethodKeys.size == METHOD_SIGNATURE_COUNT) { "Web 渲染方法签名存在遗漏或重复" }
    }

    val entries: Map<String, WebRenderCapability> = buildMap {
        putAll(methodCapabilities)
        RenderCommandType.entries.forEach { type ->
            put(commandKey(type), commandCapability(type))
        }
    }

    fun commandKey(type: RenderCommandType): String = "RenderCommandType.${type.name}"

    fun containsKey(key: String): Boolean = entries.containsKey(key)

    private fun commandCapability(type: RenderCommandType): WebRenderCapability = when (type) {
        RenderCommandType.DRAW_RICH_TEXT,
        RenderCommandType.DRAW_ITEM_STACK,
        RenderCommandType.DRAW_ENTITY_PREVIEW,
        RenderCommandType.DRAW_MODEL_PREVIEW,
        -> WebRenderCapability.PLACEHOLDER
        RenderCommandType.IMAGE_STYLED,
        RenderCommandType.NEON_GLOW,
        RenderCommandType.RIPPLE,
        RenderCommandType.PROCEDURAL_TEXTURE,
        RenderCommandType.FLOW,
        RenderCommandType.ENERGY,
        RenderCommandType.FILTER_GROUP,
        RenderCommandType.BACKDROP_FILTER,
        RenderCommandType.BLEND_GROUP,
        RenderCommandType.MATERIAL_LAYER,
        RenderCommandType.HISTORY_GROUP,
        RenderCommandType.PARTICLE,
        -> WebRenderCapability.UNSUPPORTED
        RenderCommandType.FILL_RECT,
        RenderCommandType.DRAW_TEXT,
        RenderCommandType.DRAW_LINE,
        RenderCommandType.DRAW_IMAGE,
        RenderCommandType.ROUNDED_RECT,
        RenderCommandType.GRADIENT,
        RenderCommandType.SHADOW,
        RenderCommandType.DRAW_TEXTURED_QUAD,
        -> WebRenderCapability.NATIVE
    }

    // 原 62 + history/particle 相关 6 个方法签名
    private const val METHOD_SIGNATURE_COUNT = 68
}
